import * as CSS from "csstype";
import {TinyColor, readability} from "@ctrl/tinycolor";

export class ColorPaletteWrapper {

  palette: ColorPalette

  constructor(palette: ColorPalette) {
    this.palette = palette
  }

  static single = (color: CSS.Property.Color): ColorPaletteWrapper => {
    return new ColorPaletteWrapper({
      50: color,
      100: color,
      200: color,
      300: color,
      400: color,
      500: color,
      600: color,
      700: color,
      800: color,
      900: color,
    })
  }

  get primary(): CSS.Property.Color {
    return this.palette["400"]
  }

  asArray = (): ColorPaletteArray => {
    return Object.values(this.palette) as ColorPaletteArray
  }
}

export type ColorPalette = {
  50: CSS.Property.Color,
  100: CSS.Property.Color,
  200: CSS.Property.Color,
  300: CSS.Property.Color,
  400: CSS.Property.Color,
  500: CSS.Property.Color,
  600: CSS.Property.Color,
  700: CSS.Property.Color,
  800: CSS.Property.Color,
  900: CSS.Property.Color,
}

export type ColorPaletteArray = [
  CSS.Property.Color,
  CSS.Property.Color,
  CSS.Property.Color,
  CSS.Property.Color,
  CSS.Property.Color,
  CSS.Property.Color,
  CSS.Property.Color,
  CSS.Property.Color,
  CSS.Property.Color,
  CSS.Property.Color,
]

export const randomColor = (): string => {
  const letters = "0123456789ABCDEF".split("");
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

const randomColors: CSS.Property.Color[] = [];
export const getColorByIndex = (index: number): CSS.Property.Color => {
  if (randomColors.length <= index) {
    for (let i = randomColors.length - 1; i < index; i++)
      randomColors.push(randomColor());
  }

  return randomColors[index];
};

// Shared Colors
export const grayColor: CSS.Property.Color = "#58595B";
export const errorColor: CSS.Property.Color = "#E1251B";
export const errorColors: CSS.Property.Color[] = [
  "#D8BFBE",
  "#CFA4A2",
  "#CB8885",
  "#CC6A65",
  "#D44941",
  "#E1251B",
  "#B9312A",
  "#993833",
  "#813B37",
  "#6D3B38"
];

// General Colors
export const serviceWorkOrangePalette = new ColorPaletteWrapper({
  "50": "#ffeade",
  "100": "#ffc6b0",
  "200": "#ffa27f",
  "300": "#ff7c4d",
  "400": "#FF7F50",
  "500": "#e53f02",
  "600": "#b33000",
  "700": "#812100",
  "800": "#4f1300",
  "900": "#200400"
})
export const serviceWorkOrange: CSS.Property.Color = serviceWorkOrangePalette.primary

// Dispatch Request Status Colors
export const dispatchRequestPickedUpColorPalette = ColorPaletteWrapper.single("#B6AC4D")
export const dispatchRequestPickedUpColor: CSS.Property.Color = dispatchRequestPickedUpColorPalette.primary

export const dispatchRequestPendingColorPalette = ColorPaletteWrapper.single("#febd7e")
export const dispatchRequestPendingColor: CSS.Property.Color = dispatchRequestPendingColorPalette.primary

export const dispatchRequestReassignedColorPalette = ColorPaletteWrapper.single("#E1251B")
export const dispatchRequestReassignedColor: CSS.Property.Color = dispatchRequestReassignedColorPalette.primary

export const dispatchRequestRejectedColorPalette = ColorPaletteWrapper.single("#E1251B")
export const dispatchRequestRejectedColor: CSS.Property.Color = dispatchRequestRejectedColorPalette.primary

export const dispatchRequestCanceledColorPalette = ColorPaletteWrapper.single("#E1251B")
export const dispatchRequestCanceledColor: CSS.Property.Color = dispatchRequestCanceledColorPalette.primary

export const dispatchRequestAcceptedColorPalette = ColorPaletteWrapper.single("#22A7F0")
export const dispatchRequestAcceptedColor: CSS.Property.Color = dispatchRequestAcceptedColorPalette.primary

export const dispatchRequestCompletedColorPalette = ColorPaletteWrapper.single("#52c41a")
export const dispatchRequestCompletedColor: CSS.Property.Color = dispatchRequestCompletedColorPalette.primary

export const dispatchRequestStartedColorPalette = ColorPaletteWrapper.single("#4DB6AC")
export const dispatchRequestStartedColor: CSS.Property.Color = dispatchRequestStartedColorPalette.primary

export const dispatchRequestUnresovledColorPalette = ColorPaletteWrapper.single("#FF7F7F")
export const dispatchRequestUnresovledColor: CSS.Property.Color = dispatchRequestUnresovledColorPalette.primary

// Dispatch Status Colors
export const dispatchPendingColorPalette = ColorPaletteWrapper.single("#febd7e")
export const dispatchPendingColor: CSS.Property.Color = dispatchPendingColorPalette.primary

export const dispatchCanceledColorPalette = ColorPaletteWrapper.single("#E1251B")
export const dispatchCanceledColor: CSS.Property.Color = dispatchCanceledColorPalette.primary

export const dispatchCompletedColorPalette = ColorPaletteWrapper.single("#52c41a")
export const dispatchCompletedColor: CSS.Property.Color = dispatchCompletedColorPalette.primary

export const dispatchStartedColorPalette = ColorPaletteWrapper.single("#4DB6AC")
export const dispatchStartedColor: CSS.Property.Color = dispatchStartedColorPalette.primary

/**
 * Site Type Colors
 */
export const geofenceColor: CSS.Property.Color = "#4c4c4c";

export const siteCentralColorPalette = ColorPaletteWrapper.single("#00CD66")
export const siteCentralColor: CSS.Property.Color = siteCentralColorPalette.primary

export const sitePadColorPalette = ColorPaletteWrapper.single("#4cdc93")
export const sitePadColor: CSS.Property.Color = siteCentralColorPalette.primary

export const sitePondColorPalette = ColorPaletteWrapper.single("#6897BB")
export const sitePondColor: CSS.Property.Color = siteCentralColorPalette.primary

export const siteDisposalColorPalette = ColorPaletteWrapper.single("#7373ff")
export const siteDisposalColor: CSS.Property.Color = siteDisposalColorPalette.primary

export const siteYardColorPalette = ColorPaletteWrapper.single("#FFC12B")
export const siteYardColor: CSS.Property.Color = siteYardColorPalette.primary

export const siteStagingColorPalette = ColorPaletteWrapper.single("#8f8672")
export const siteStagingColor: CSS.Property.Color = siteStagingColorPalette.primary

export const siteDefaultColorPalette = ColorPaletteWrapper.single("#000000")
export const siteDefaultColor: CSS.Property.Color = siteDefaultColorPalette.primary

/**
 * Company Type Colors
 */
export const companyOperatorColor: CSS.Property.Color = "#4cdc93";
export const companyVendorColor: CSS.Property.Color = "#FF7373";
export const companyTruckColor: CSS.Property.Color = "#FFC12B";
export const companyDefaultColor: CSS.Property.Color = "#000000";

export const defaultMapMarkerColor: CSS.Property.Color = "#FE7569";

export const geofenceEventColor: CSS.Property.Color = "#EE82EE";

export const gpsMarkerColorPalette = ColorPaletteWrapper.single("#e7d1e7")
export const gpsMarkerColor: CSS.Property.Color = gpsMarkerColorPalette.primary

export const gpsRouteColor: CSS.Property.Color = "#FFFF00";

export const shiftStartColor: CSS.Property.Color = "#26E227";
export const shiftStartColors: CSS.Property.Color[] = [
  "#CADFCA",
  "#AED5AE",
  "#91CF91",
  "#71CE72",
  "#4ED44F",
  "#26E227",
  "#2EBF2F",
  "#379F37",
  "#3B863B",
  "#3B723C"
];

export const shiftEndColor: CSS.Property.Color = "#E22726";

/**
 * Transport Colors
 */
export const transportPickupColor: CSS.Property.Color = "#FFBF00";
export const transportDeliveryColor: CSS.Property.Color = "#FF4000";
export const transportDefaultColor: CSS.Property.Color = "#000000";

/* Various colors for transports */
export const transportLineColors: CSS.Property.Color[] = [
  "#2da711",
  "#f96837",
  "#e52933",
  "#2c1c90",
  "#b5e8f6",
  "#029b87",
  "#ab3434",
  "#85ff00",
  "#aaaaaa",
  "#00c3ff",
  "#ffd008",
  "#b96eff"
];

/**
 * Container Alert Colors
 */
export const containerAlertHighCriticalColor: CSS.Property.Color = "#E1251B";

export const containerAlertHighColorPalette = ColorPaletteWrapper.single("#E1251B")
export const containerAlertHighColor: CSS.Property.Color = containerAlertHighColorPalette.primary;

export const containerAlertLowCriticalColor: CSS.Property.Color = "#ff4d00";
export const containerAlertLowColor: CSS.Property.Color = "#ff4d00";
export const containerAlertNormalColor: CSS.Property.Color = "#52c41a";
export const containerAlertInternDecreaseColor: CSS.Property.Color = "#ffff00";
export const containerAlertInternIncreaseColor: CSS.Property.Color = "#ffff00";
export const containerAlertScadaSilenceColor: CSS.Property.Color = "#b300ff";
export const containerAlertBadScadaColor: CSS.Property.Color = "#b300ff";
export const containerAlertStickingColor: CSS.Property.Color = "#80461B";
export const containerAlertMinimumIncreaseColor: CSS.Property.Color = "#ffc800";
export const containerAlertMinimumDecreaseColor: CSS.Property.Color = "#ffc800";
export const containerAlertUnchangedLevelColor: CSS.Property.Color = "#00e5ff";
export const containerAlertOilPullColor: CSS.Property.Color = "#000";

/**
 * Site Alert Colors
 */
export const siteAlertHighH2SColor: CSS.Property.Color = "#FF7F50";

/**
 * Cargo Subtype Colors
 */
export const cargoSubtypeWaterColorPalette = ColorPaletteWrapper.single("#22A7F0")
export const cargoSubtypeWaterColor: CSS.Property.Color = cargoSubtypeWaterColorPalette.primary;

export const cargoSubtypeOilColorPalette = ColorPaletteWrapper.single("#000000")
export const cargoSubtypeOilColor: CSS.Property.Color = cargoSubtypeOilColorPalette.primary;

export const cargoSubtypeChemicalsColorPalette = ColorPaletteWrapper.single("#fcba03")
export const cargoSubtypeChemicalsColor: CSS.Property.Color = cargoSubtypeChemicalsColorPalette.primary;

export const cargoSubtypeWasteColorPalette = ColorPaletteWrapper.single("#8d9e70")
export const cargoSubtypeWasteColor: CSS.Property.Color = cargoSubtypeWasteColorPalette.primary;

export const cargoSubtypeTankColorPalette = ColorPaletteWrapper.single("#a3a19d")
export const cargoSubtypeTankColor: CSS.Property.Color = cargoSubtypeTankColorPalette.primary;

/**
 * Transport Category Colors
 */
export const transportCategoryWaterColorPalette = ColorPaletteWrapper.single("#22A7F0")
export const transportCategoryWaterColor: CSS.Property.Color = transportCategoryWaterColorPalette.primary

export const transportCategoryOilColorPalette = ColorPaletteWrapper.single("#000000")
export const transportCategoryOilColor: CSS.Property.Color = transportCategoryOilColorPalette.primary

/**
 * Activity Period Status Colors
 */
export const activityPeriodStatusRawColorPalette = ColorPaletteWrapper.single("#58595B")
export const activityPeriodStatusRawColor: CSS.Property.Color = activityPeriodStatusRawColorPalette.primary

export const activityPeriodStatusAdminReviewedColorPalette = ColorPaletteWrapper.single("#fff000")
export const activityPeriodStatusAdminReviewedColor: CSS.Property.Color = activityPeriodStatusAdminReviewedColorPalette.primary

export const activityPeriodStatusVendorEnteredColorPalette = ColorPaletteWrapper.single("#fc3d39")
export const activityPeriodStatusVendorEnteredColor: CSS.Property.Color = activityPeriodStatusVendorEnteredColorPalette.primary

export const activityPeriodStatusAdminCompleteColorPalette = ColorPaletteWrapper.single("#22A7F0")
export const activityPeriodStatusAdminCompleteColor: CSS.Property.Color = activityPeriodStatusAdminCompleteColorPalette.primary

export const activityPeriodStatusOperatorValidatedColorPalette = ColorPaletteWrapper.single("#C660D6")
export const activityPeriodStatusOperatorValidatedColor: CSS.Property.Color = activityPeriodStatusOperatorValidatedColorPalette.primary

export const activityPeriodStatusVendorApprovedColorPalette = ColorPaletteWrapper.single("#52c41a")
export const activityPeriodStatusVendorApprovedColor: CSS.Property.Color = activityPeriodStatusVendorApprovedColorPalette.primary

export const activityPeriodStatusOperatorCodedColorPalette = ColorPaletteWrapper.single("#000000")
export const activityPeriodStatusOperatorCodedColor: CSS.Property.Color = activityPeriodStatusOperatorCodedColorPalette.primary

/**
 * Service Status Colors
 */
export const refServiceStatusNeedsServiceColorPalette = ColorPaletteWrapper.single("#FF3B30")
export const refServiceStatusNeedsServiceColor: CSS.Property.Color = refServiceStatusNeedsServiceColorPalette.primary

export const refServiceStatusBlockedColorPalette = ColorPaletteWrapper.single("#FF3B30")
export const refServiceStatusBlockedColor: CSS.Property.Color = refServiceStatusBlockedColorPalette.primary

export const refServiceStatusInProgressColorPalette = ColorPaletteWrapper.single("#FF9500")
export const refServiceStatusInProgressColor: CSS.Property.Color = refServiceStatusInProgressColorPalette.primary

export const refServiceStatusUnknownColorPalette = ColorPaletteWrapper.single("#8E8E93")
export const refServiceStatusUnknownColor: CSS.Property.Color = refServiceStatusUnknownColorPalette.primary

export const refServiceStatusTreatedColorPalette = ColorPaletteWrapper.single("#34C759")
export const refServiceStatusTreatedColor: CSS.Property.Color = refServiceStatusTreatedColorPalette.primary

export const refServiceStatusClearColorPalette = ColorPaletteWrapper.single("#34C759")
export const refServiceStatusClearColor: CSS.Property.Color = refServiceStatusClearColorPalette.primary

export const refServiceStatusClosedColorPalette = ColorPaletteWrapper.single("#34C759")
export const refServiceStatusClosedColor: CSS.Property.Color = refServiceStatusClosedColorPalette.primary

export const refServiceStatusOpenColorPalette = ColorPaletteWrapper.single("#34C759")
export const refServiceStatusOpenColor: CSS.Property.Color = refServiceStatusOpenColorPalette.primary

/**
 * Warning Colors
 */
export const warningLowColorPalette = ColorPaletteWrapper.single("#666666")
export const warningLowColor: CSS.Property.Color = warningLowColorPalette.primary

export const warningMediumColorPalette = ColorPaletteWrapper.single("#FF7F50")
export const warningMediumColor: CSS.Property.Color = warningMediumColorPalette.primary

export const warningHighColorPalette = ColorPaletteWrapper.single("#E22726")
export const warningHighColor: CSS.Property.Color = warningHighColorPalette.primary

/**
 * Notice Severity Colors
 */
export const noticeSeverityDefaultColorPalette = ColorPaletteWrapper.single("#666666")
export const noticeSeverityDefaultColor: CSS.Property.Color = noticeSeverityDefaultColorPalette.primary

export const noticeSeverityInfoColorPalette = ColorPaletteWrapper.single("#22A7F0")
export const noticeSeverityInfoColor: CSS.Property.Color = noticeSeverityInfoColorPalette.primary

export const noticeSeverityMajorColorPalette = ColorPaletteWrapper.single("#FF7F50")
export const noticeSeverityMajorColor: CSS.Property.Color = noticeSeverityMajorColorPalette.primary

export const noticeSeverityCriticalColorPalette = ColorPaletteWrapper.single("#E22726")
export const noticeSeverityCriticalColor: CSS.Property.Color = noticeSeverityCriticalColorPalette.primary


export const adjustBackgroundForTextContrast = (
  textColor: string,
  backgroundColor: string,
  minContrast = 4.5
): string => {
  const text = new TinyColor(textColor);
  let bg = new TinyColor(backgroundColor);

  if (readability(textColor, bg) >= minContrast) {
    return bg.toHexString();
  }

  const textIsLight = text.isLight();

  for (let i = 0; i < 20; i++) {
    bg = textIsLight ? bg.darken(3) : bg.lighten(3);

    if (readability(textColor, bg) >= minContrast) {
      return bg.toHexString();
    }
  }

  return bg.toHexString();
}
